<script lang="ts">
import type { InputProps, SelectMenuItem, SelectMenuSlots, SelectMenuEmits as USelectMenuEmits, SelectMenuProps as USelectMenuProps } from '#ui/types';
import type { ArrayOrNested, GetItemKeys } from '#ui/types/utils';
import { useFormField } from '#layers/ui/composables/use-form-field';

export interface SelectMenuProps<T extends ArrayOrNested<SelectMenuItem> = ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false> extends Omit<USelectMenuProps<T, VK, M>, 'clear'> {
	/**
	 * Show a button (and Backspace shortcut) to clear the value. On clear the
	 * value resets to `null` (or an empty array when `multiple`).
	 */
	clear?: boolean;
	disabled?: boolean;
	readonly?: boolean;
}

export interface SelectMenuEmits<T extends ArrayOrNested<SelectMenuItem> = ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false> extends USelectMenuEmits<T, VK, M> {
	'clear': [];
	'update:modelValue': USelectMenuEmits<T, VK, M>['update:modelValue'];
}
</script>

<script setup lang="ts" generic="T extends ArrayOrNested<SelectMenuItem>, VK extends GetItemKeys<T> | undefined = undefined, M extends boolean = false">
import { reactiveOmit } from '@vueuse/core';
import { useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';

import { useReadonly } from '#layers/ui/composables/use-readonly';
import { mergeUi } from '#layers/ui/utils/merge-ui';
import SelectMenu from '#ui/components/SelectMenu.vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<SelectMenuProps<T, VK, M>>(), {
	portal: true,
	searchInput: true,
	resetSearchTermOnBlur: true,
	resetSearchTermOnSelect: true,
	resetModelValueOnClear: true,
	highlightOnHover: true,
});
const emits = defineEmits<SelectMenuEmits<T, VK, M>>();
const slots = defineSlots<SelectMenuSlots<T, VK, M>>();
const forwarded = useForwardPropsEmits(reactiveOmit(props, ['clear', 'readonly']), emits);

const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);
const searchTerm = defineModel<string>('searchTerm', { default: '' });

const { emitFormInput, emitFormChange } = useFormField<InputProps>(props, { forwardContext: true });

function isValueEmpty(value: unknown) {
	return value == null || value === '' || (Array.isArray(value) && value.length === 0);
}

const isClearShown = computed(
	() => !props.disabled && !props.readonly && props.clear && !isValueEmpty(props.modelValue),
);

function onClearValue() {
	emits('update:modelValue', (props.multiple ? [] : null) as never);
	emits('clear');
	emitFormChange();
	emitFormInput();
}

function onReadonlyCapture(event: KeyboardEvent) {
	if (preventKeyboardNavigation(event, ['Tab'])) return;

	if (event.key === 'Backspace' && !props.disabled && isClearShown.value) {
		onClearValue();
	}
}
</script>

<template>
	<SelectMenu
		v-bind="{ ...forwarded, ...$attrs }"
		v-model:search-term="searchTerm"
		:ui="mergeUi(props.ui, {
			base: [isClearShown ? 'pe-7.5' : '', props.readonly ? 'cursor-default' : ''],
			trailing: isClearShown ? 'pe-1' : '',
		})"
		@pointerdown.capture="preventPointer"
		@click.capture="preventPointer"
		@keydown.capture="onReadonlyCapture"
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>
		<template v-if="isClearShown" #trailing>
			<DTooltip :text="$t('common.clear')">
				<DButton
					v-if="modelValue"
					data-testid="select-clear"
					icon="lucide:x"
					color="neutral"
					variant="link"
					size="sm"
					@click.stop="onClearValue()"
				/>
			</DTooltip>
		</template>
	</SelectMenu>
</template>
